import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { formatINR } from "@/components/shared";
import { Printer, ArrowLeft } from "lucide-react";

// -------- Indian number-to-words --------------------------------------------
import { formatDate, formatDateTime } from "@/lib/datetime";
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function belowThousand(n) {
  if (n === 0) return "";
  if (n < 20) return ONES[n];
  if (n < 100) return TENS[Math.floor(n / 10)] + (n % 10 ? " " + ONES[n % 10] : "");
  return ONES[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + belowThousand(n % 100) : "");
}

function inrInWords(amount) {
  if (amount === null || amount === undefined) return "";
  const rounded = Math.round(amount * 100);
  const rupees = Math.floor(rounded / 100);
  const paise = rounded % 100;
  if (rupees === 0 && paise === 0) return "Rupees Zero Only";
  const parts = [];
  const crore = Math.floor(rupees / 10000000);
  const lakh = Math.floor((rupees % 10000000) / 100000);
  const thousand = Math.floor((rupees % 100000) / 1000);
  const rest = rupees % 1000;
  if (crore) parts.push(belowThousand(crore) + " Crore");
  if (lakh) parts.push(belowThousand(lakh) + " Lakh");
  if (thousand) parts.push(belowThousand(thousand) + " Thousand");
  if (rest) parts.push(belowThousand(rest));
  let words = "Rupees " + parts.join(" ");
  if (paise) words += " and " + belowThousand(paise) + " Paise";
  return words + " Only";
}

const STATUS_TONE = {
  draft: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  received: "bg-blue-50 text-blue-800 border-blue-200",
  cancelled: "bg-gray-100 text-gray-500 border-gray-200",
};

export default function POPrint() {
  const { id } = useParams();
  const [po, setPo] = useState(null);
  const [rmsById, setRmsById] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, rms] = await Promise.all([
          api.get(`/production/purchase-orders/${id}`),
          api.get("/production/raw-materials"),
        ]);
        setPo(p.data);
        const map = {};
        (rms.data || []).forEach((r) => { map[r.id] = r; });
        setRmsById(map);
      } finally { setLoading(false); }
    })();
  }, [id]);

  useEffect(() => {
    if (!loading && po) {
      setTimeout(() => window.print(), 400);
    }
  }, [loading, po]);

  if (loading) return <div className="p-8 text-gray-500">Loading…</div>;
  if (!po) return <div className="p-8 text-red-600">Purchase Order not found</div>;

  const anyReceived = (po.items || []).some((l) => (l.received_qty || 0) > 0);

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
        }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-white border-b border-gray-200 p-3 flex items-center gap-3 shadow-sm">
        <button onClick={() => window.history.back()} className="h-9 w-9 rounded-md border border-gray-200 hover:bg-gray-50 flex items-center justify-center">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="font-heading font-semibold text-gray-900">Print Preview — {po.po_no}</span>
        <span className={`text-[11px] font-semibold border rounded px-2 py-0.5 ${STATUS_TONE[po.status] || STATUS_TONE.draft}`}>{po.status}</span>
        <button onClick={() => window.print()} className="ml-auto h-9 px-3 rounded-md bg-[#9B1C1C] hover:bg-[#7F1D1D] text-white text-sm font-semibold flex items-center gap-1.5" data-testid="po-print-btn">
          <Printer className="h-4 w-4" /> Print
        </button>
      </div>

      <div className="max-w-[210mm] mx-auto bg-white shadow-sm print:shadow-none p-8 my-6 print:my-0 print:p-0 text-gray-900">
        {/* Header */}
        <div className="flex justify-between items-start border-b-2 border-[#9B1C1C] pb-4 mb-5">
          <div>
            <h1 className="text-2xl font-heading font-bold text-[#9B1C1C]">Thara Gold Covering</h1>
            <p className="text-xs text-gray-600 mt-1">Imitation Jewellery — Manufacturer & Wholesaler</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Purchase Order</p>
            <p className="text-lg font-heading font-bold text-gray-900">{po.po_no}</p>
            {po.status === "draft" && <p className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold mt-0.5">DRAFT — Not Yet Approved</p>}
            {po.status === "cancelled" && <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mt-0.5">CANCELLED</p>}
          </div>
        </div>

        {/* Vendor + Meta */}
        <div className="grid grid-cols-2 gap-6 mb-5 text-sm">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-1">Vendor</p>
            <p className="font-semibold text-base">{po.vendor_name}</p>
            {po.vendor_gst && <p className="text-xs mt-1"><span className="text-gray-500">GST:</span> <span className="font-mono">{po.vendor_gst}</span></p>}
          </div>
          <div>
            <table className="w-full text-xs">
              <tbody>
                <tr><td className="py-0.5 text-gray-500 uppercase tracking-widest text-[10px]">Order Date</td><td className="py-0.5 font-mono">{formatDate(po.po_date)}</td></tr>
                {po.delivery_date && <tr><td className="py-0.5 text-gray-500 uppercase tracking-widest text-[10px]">Delivery Date</td><td className="py-0.5 font-mono">{formatDate(po.delivery_date)}</td></tr>}
                {po.reference_no && <tr><td className="py-0.5 text-gray-500 uppercase tracking-widest text-[10px]">Reference</td><td className="py-0.5 font-mono">{po.reference_no}</td></tr>}
                <tr><td className="py-0.5 text-gray-500 uppercase tracking-widest text-[10px]">Payment</td><td className="py-0.5 font-semibold">{po.payment_status}</td></tr>
                {po.credit_period_days > 0 && <tr><td className="py-0.5 text-gray-500 uppercase tracking-widest text-[10px]">Credit Days</td><td className="py-0.5 font-mono">{po.credit_period_days}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Line items */}
        <table className="w-full text-xs border border-gray-300 mb-5">
          <thead className="bg-gray-100">
            <tr>
              <th className="border border-gray-300 px-2 py-1.5 text-left w-8">#</th>
              <th className="border border-gray-300 px-2 py-1.5 text-left">Description</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-16">HSN</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-14">Unit</th>
              <th className="border border-gray-300 px-2 py-1.5 text-right w-16">Qty</th>
              {anyReceived && <th className="border border-gray-300 px-2 py-1.5 text-right w-20">Received</th>}
              <th className="border border-gray-300 px-2 py-1.5 text-right w-20">Rate</th>
              <th className="border border-gray-300 px-2 py-1.5 text-right w-20">Discount</th>
              <th className="border border-gray-300 px-2 py-1.5 text-right w-14">Tax %</th>
              <th className="border border-gray-300 px-2 py-1.5 text-right w-20">Tax Amt</th>
              <th className="border border-gray-300 px-2 py-1.5 text-right w-24">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(po.items || []).map((l, i) => {
              const rm = rmsById[l.raw_material_id];
              return (
                <tr key={i}>
                  <td className="border border-gray-300 px-2 py-1 text-center">{i + 1}</td>
                  <td className="border border-gray-300 px-2 py-1"><div className="font-medium">{l.raw_material_name}</div></td>
                  <td className="border border-gray-300 px-2 py-1 text-center font-mono">{rm?.hsn_code || "—"}</td>
                  <td className="border border-gray-300 px-2 py-1 text-center">{l.unit}</td>
                  <td className="border border-gray-300 px-2 py-1 text-right tabular-nums">{l.qty}</td>
                  {anyReceived && <td className="border border-gray-300 px-2 py-1 text-right tabular-nums">{l.received_qty || 0}</td>}
                  <td className="border border-gray-300 px-2 py-1 text-right tabular-nums">{formatINR(l.purchase_price)}</td>
                  <td className="border border-gray-300 px-2 py-1 text-right tabular-nums">{l.discount ? formatINR(l.discount) : "—"}</td>
                  <td className="border border-gray-300 px-2 py-1 text-right tabular-nums">{l.tax_pct || "—"}</td>
                  <td className="border border-gray-300 px-2 py-1 text-right tabular-nums">{l.tax_amount ? formatINR(l.tax_amount) : "—"}</td>
                  <td className="border border-gray-300 px-2 py-1 text-right tabular-nums font-semibold">{formatINR(l.line_amount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals + amount in words */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-1">Amount in Words</p>
            <p className="text-sm font-semibold italic border-b border-gray-300 pb-2">{inrInWords(po.net_amount)}</p>
            {po.terms && (
              <div className="mt-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-1">Terms & Conditions</p>
                <p className="text-xs text-gray-700 whitespace-pre-line">{po.terms}</p>
              </div>
            )}
            {po.payment_notes && (
              <div className="mt-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-1">Payment Notes</p>
                <p className="text-xs text-gray-700">{po.payment_notes}</p>
              </div>
            )}
          </div>
          <div>
            <table className="w-full text-sm">
              <tbody>
                <tr><td className="py-1 text-gray-600">Subtotal</td><td className="py-1 text-right tabular-nums font-mono">{formatINR(po.subtotal)}</td></tr>
                {po.header_discount > 0 && <tr><td className="py-1 text-gray-600">Header Discount</td><td className="py-1 text-right tabular-nums font-mono">−{formatINR(po.header_discount)}</td></tr>}
                {po.tax_total > 0 && <tr><td className="py-1 text-gray-600">Tax (GST)</td><td className="py-1 text-right tabular-nums font-mono">+{formatINR(po.tax_total)}</td></tr>}
                <tr className="border-t-2 border-gray-900">
                  <td className="py-2 font-bold text-lg">Net Amount</td>
                  <td className="py-2 text-right tabular-nums font-heading font-bold text-lg">{formatINR(po.net_amount)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Signatory */}
        <div className="mt-16 grid grid-cols-2 gap-6 text-xs">
          <div>
            <p className="border-t border-gray-400 pt-1 text-center text-gray-500">Prepared By</p>
          </div>
          <div>
            <p className="border-t border-gray-400 pt-1 text-center text-gray-500">For Thara Gold Covering — Authorized Signatory</p>
          </div>
        </div>
      </div>
    </div>
  );
}
